import React, { useContext, useState, useEffect } from 'react'
import { Link,  useHistory } from 'react-router-dom'
import { App, Button, Table, Tooltip, Switch, Breadcrumb, Radio, Alert, Typography } from 'antd'
import { WarningTwoTone, DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined, ToolTwoTone } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import * as logger from '../../services/logger'
import { getAutomations, getLeaveTypeListShort, getSeniorityEntitlementAddedDays, getUserName } from '../../graphql/custom-queries'
import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../circular-progress'
import { notificationStore } from '../../context/notificationsContext/store'
import HandleSubscribeAutomations from '../handle-subscribe-automations'

import { track } from '../../services/analytics/analytics'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import EmptyData from '../empty-data'

import { IAutomations, IGetAutomations, IGetLeaveTypeListShort, IGetSeniorityEntitlementAddedDays, IGetUserName, ILeaveTypeListShort } from '../../types/custom-queries'
import { ITableSort } from '../../types/data'
import { FrontendUrls } from '../../types/urls'
import { IAutomationSeniorityEntitlementForm, IAutomationViewPage } from '@vacationtracker/shared/types/automations'
import { AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import capitalize from 'lodash/capitalize'

const { Text } = Typography

const SeniorityEntitlementView = ({
  formatMessage,
  amIAdmin,
  deleteAutomation,
  authUserId,
  dateFormat,
  handleSubscribe,
  status,
  addonDetails,
  currentPlan,
}: IAutomationViewPage) => {
  const history = useHistory()
  const { message, modal, notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [isLoading, setIsLoading] = useState(true)
  const [activeAutomationStatus, setActiveAutomationStatus] = useState<{id: string; isActive: boolean} | null>(null)
  const [sortedInfo, setSortedInfo] = useState<ITableSort>({
    columnKey: 'name',
    order: 'ascend',
  })
  const [automations, setAutomations] = useState<IAutomations[]>([])
  const [correlationId, setCorrelationId] = useState<string | null>()
  const [deleteAutomationId, setDeleteAutomationId] = useState<string | null>(null)
  const [leaveTypeList, setLeaveTypeList] = useState<ILeaveTypeListShort[] | []>([])
  const [, forceUpdate] = useState({})
  
  const [getAutomationsQuery] = useManualQuery<IGetAutomations>(getAutomations)
  const [getSeniorityEntitlementAddedDaysQuery] = useManualQuery<IGetSeniorityEntitlementAddedDays>(getSeniorityEntitlementAddedDays)
  const [getLeaveTypeListShortQuery] = useManualQuery<IGetLeaveTypeListShort>(getLeaveTypeListShort)
  const [getUserNameQuery] = useManualQuery<IGetUserName>(getUserName)

  useEffect(() => {
    fetchData()
    if (correlationId && !actionNotifications.includes(correlationId) && activeAutomationStatus) {
      setActiveAutomationStatus(null)
      setCorrelationId(null)
      setDeleteAutomationId(null)
    }
  }, [actionNotifications])

  // Auto-enable delete button after 5 minutes for recently created automations
  useEffect(() => {
    if (automations.length === 0) return

    // Find the most recently created automation
    const recentAutomation = automations
      .filter(a => a.createdAt && a.createdAt !== '')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]

    if (!recentAutomation) return

    const timeSinceCreation = Date.now() - new Date(recentAutomation.createdAt).getTime()
    const fiveMinutes = 5 * 60 * 1000

    // If within 5 minutes, set a timeout to force update when 5 minutes pass
    if (timeSinceCreation < fiveMinutes) {
      const timeUntilEnable = fiveMinutes - timeSinceCreation
      const timeoutId = setTimeout(() => {
        forceUpdate({}) // Force re-render to update button state
      }, timeUntilEnable)

      return () => clearTimeout(timeoutId)
    }
  }, [automations])

  const fetchData = async () => {
    const response = await getAutomationsQuery()
    if (!response.data || response.error) throw response.error
    setAutomations(response.data.getAutomations.filter(automation => automation.automationType === 'SENIORITY_ENTITLEMENT'))
    
    const leaveTypeResponse = await getLeaveTypeListShortQuery()
    if (!leaveTypeResponse.data || leaveTypeResponse.error) throw leaveTypeResponse.error
    setLeaveTypeList(leaveTypeResponse.data.getLeaveTypeList)
  
    setIsLoading(false)
  }

  const createAuomation = () => {
    history.push(`${FrontendUrls.addons}/seniority-entitlement/create`)
  }

  const fetchSeniorityEntitlementAddedDaysQuery = async (automationId: string) => {
    const response = await getSeniorityEntitlementAddedDaysQuery({ variables: {
      limit: 1,
      automationId,
    }})
    if (!response.data || response.error) throw response.error
    
    return response.data.getUsersSeniorityEntitlementAddedDays
  }

  const showConfirmDelete = async (row: IAutomations, event) => {
    event.stopPropagation()
    const response = await fetchSeniorityEntitlementAddedDaysQuery(row.id)
    const usersWithSeniorityEntitlementAddedDaysTotal = response.totalUsers

    const usersName: { id: string; amount: number; units: string; name: string }[] = []
    for (const user of response.users) {
      const userNameResponse = await getUserNameQuery({ variables: {
        userId: user.id,
      }})
      if (!userNameResponse.data || userNameResponse.error) throw userNameResponse.error
      usersName.push({
        id: user.id,
        amount: user.amount,
        units: user.units,
        name: userNameResponse.data.getUser.name,
      })
    }
    
    let deleteSeniorityEntitlementAddedDays = false

    modal.confirm({
      title: formatMessage({ id: 'notifications.deleteAutomationTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: <>
        {formatMessage(
          { id: 'notifications.deleteAutomationConfirm' },
          { name: row.name, strong: (...chunks) => <strong>{chunks}</strong> }
        )}<br/>
        {usersWithSeniorityEntitlementAddedDaysTotal > 0 &&
        <>
          <br/>
          <Alert 
            message={formatMessage({ 
              id: 'notifications.usersDaysAlreadyAwarded', 
            }, { 
              totalUsers: usersWithSeniorityEntitlementAddedDaysTotal,
              b: (...chunks) => (
                <Text strong>{chunks}</Text>
              ),
            })} 
            type="warning" 
          /><br/>
          <Text>{formatMessage({ id: 'notifications.keepTheseAwardedDays' })}</Text>
          <Radio.Group
            defaultValue={deleteSeniorityEntitlementAddedDays}
            style={{
              paddingTop: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
            onChange={(e) => {
              deleteSeniorityEntitlementAddedDays = e.target.value as boolean
            }}
            options={[
              {
                value: false,
                label: (
                  <>
                    <Text strong>{formatMessage({ id: 'app.yes' })}</Text>
                    {usersName.length > 0 && (
                      <Text type="secondary">
                        {' — '}
                        {formatMessage({ id: 'app.example.short' })}
                        {usersName.slice(0, 3).map((user, index) => (
                          <span key={user.id}>
                            {index > 0 && ', '}
                            {user.name} {formatMessage({ id: 'notifications.keeps' })} +{user.amount} {user.units}
                          </span>
                        ))}
                        {usersName.length > 3 && ` ${formatMessage({ id: 'app.andMore' }, { count: usersName.length - 3 })}`}
                      </Text>
                    )}
                  </>
                ),
              },
              {
                value: true,
                label: (
                  <>
                    <Text strong>{formatMessage({ id: 'app.no' })}</Text>
                    {usersName.length > 0 && (
                      <Text type="secondary">
                        {' — '}
                        {formatMessage({ id: 'app.example.short' })}
                        {usersName.slice(0, 3).map((user, index) => (
                          <span key={user.id}>
                            {index > 0 && ', '}
                            {user.name} {formatMessage({ id: 'notifications.loses' })} +{user.amount} {user.units}
                          </span>
                        ))}
                        {usersName.length > 3 && ` ${formatMessage({ id: 'app.andMore' }, { count: usersName.length - 3 })}`}
                      </Text>
                    )}
                  </>
                ),
              },
            ]}
          />
        </>
        }
      </>,
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      width: 500,
      maskClosable: true,
      onOk() {
        deleteAutomation(row.id as string, row.name as string, row.automationType as string, { deleteSeniorityEntitlementAddedDays })
      },
      onCancel() {
        setDeleteAutomationId(null)
      },
    })
  }

  const updateAutomations = async (row, status) => {
    setActiveAutomationStatus({
      id: row.id,
      isActive: status,
    })
    track('AUTOMATION_TOGGLE_CHANGED', {
      automationType: row.automationType,
      automationStatus: status ? 'enabled' : 'disabled',
    })

    const response = await Api.post('/core/event', {
      eventType: 'SENIORITY_ENTITLEMENT_UPDATED',
      eventGroup: 'AUTOMATION',
      name: row.name,
      leaveTypeId: row.leaveTypeId,
      calculateFrom: row.calculateFrom ? row.calculateFrom : 'TODAY',
      periods: row.periods,
      locations: row.locations,
      teams: row.teams,
      labels: row.labels,
      prorate: row.prorate,
      automationId: row.id,
      isActive: status,
    })
    setCorrelationId(response.correlationId as string)

    notification.open({
      key: response.correlationId,
      message: formatMessage({ id: 'automations.seniorityEntitlement.updateInProgress' }),
      icon: (<LoadingOutlined />),
      duration: 0,
    })
    setActionNotifications([ ...actionNotifications, response.correlationId ])
  }

  const confirmUpdateAutomations = (row, checked) => {
    if (!checked) {
      modal.confirm({
        title: formatMessage({ id: 'notifications.updateAutomationTitle' }),
        icon: <ExclamationCircleOutlined />,
        content: formatMessage({ id: 'notifications.updateAutomationConfirm' }),
        okText: formatMessage({ id: 'app.ok' }),
        okType: 'danger',
        width: 500,
        maskClosable: true,
        onOk() {
          updateAutomations(row, checked)
        },
      })
    } else {
      updateAutomations(row, checked)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTableChange = (pagination, filters, sorter: ITableSort) => {
    setSortedInfo(sorter)
  }

  const automationsColumns = [
    {
      title: <IntlMessages id="app.active" />,
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      sorter: ((a, b) => b.isActive - a.isActive),
      sortOrder: (sortedInfo.columnKey === 'isActive' && sortedInfo.order) || null,
      render: (id: string, row) => {
        if (row.error) {
          return (
            <Tooltip title={<IntlMessages id={row.error} />}>
              <div style={{ width: '100%', height: '100', textAlign: 'center'}}>
                <WarningTwoTone twoToneColor="red" style={{ fontSize: 20 }} />
              </div>
            </Tooltip>
          )
        }
        return (
          <Switch checked={row.isActive}
            loading={Boolean(activeAutomationStatus && activeAutomationStatus.id === row.id)}
            onClick={(checked, event) => {
              event.stopPropagation()
              confirmUpdateAutomations(row, checked)
            }}
            disabled={(!amIAdmin && authUserId !== row.owner?.id) || status === AddonsStatusEnum.CANCELED}
          />
        )
      },
    },
    {
      title: <IntlMessages id="app.name" />,
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: (sortedInfo.columnKey === 'name' && sortedInfo.order) || null,
    },
    {
      title: <IntlMessages id="app.leaveType" />,
      dataIndex: 'leaveTypeId',
      key: 'leaveTypeId',
      render: (leaveTypeId: string) => {
        return leaveTypeList.find((leaveType: ILeaveTypeListShort) => leaveType.id === leaveTypeId)?.name || <IntlMessages id="app.deleted" />
      },
    },
    {
      title: <IntlMessages id="automationsForm.period" />,
      dataIndex: 'periods',
      key: 'periods',
      render: (periods: IAutomationSeniorityEntitlementForm['periods']) => {
        return periods.map((period, index) => {
          return (
            <span key={index}>
              <IntlMessages key={index} id={`automations.seniorityEntitlement.periodsViewTable${capitalize(period.units)}`}
                values={{ amount: period.amount, years: period.years }} />
              {index === periods.length - 1 ? '' : <>,<br/></>}
            </span>
          )
        })
      },
    },
    {
      title: <IntlMessages id="app.created" />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      sortOrder: (sortedInfo.columnKey === 'createdAt' && sortedInfo.order) || null,
      render: (createdAt) => <FormattedDate value={createdAt.split('T')[0]} format={dateFormat} />,
    },
    {
      title: '',
      className: 'action',
      width: 78,
      dataIndex: 'id',
      key: 'id',
      render: (id: string, row: IAutomations) => {
        const isRecentlyCreated = row.createdAt && row.createdAt !== '' ? (Date.now() - new Date(row.createdAt).getTime()) < 5 * 60 * 1000 : false
        return (
          <Tooltip title={isRecentlyCreated ? <IntlMessages id="automations.seniorityEntitlement.deleteAutomationTooltip" /> : undefined}>
            <Button
              type="link"
              disabled={(!amIAdmin && authUserId !== row.owner?.id) || status === AddonsStatusEnum.CANCELED || (Boolean(row.createdAt) && isRecentlyCreated)}
              onClick={(event) => {
                setDeleteAutomationId(row.id)
                showConfirmDelete(row, event)
              }}
              loading={Boolean(deleteAutomationId && deleteAutomationId === id)}
            >
              {!(deleteAutomationId && deleteAutomationId === id) && <DeleteOutlined />}
            </Button>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <IntlMessages id="automations.SENIORITY_ENTITLEMENT" />
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <Link to={FrontendUrls.addons}><IntlMessages id="sidebar.addons" /></Link>,
              },
              {
                title: <IntlMessages id="automations.SENIORITY_ENTITLEMENT" />,
              },
            ]}
          />
        </div>
      </div>
      <div className="main-content-body">
        {amIAdmin &&
          <HandleSubscribeAutomations
            handleSubscribe={handleSubscribe}
            status={status}
            automationType="SENIORITY_ENTITLEMENT"
            formatMessage={formatMessage}
            includedInCurrentPlan={addonDetails?.includedIn?.includes(currentPlan ?? '') || false}
          />
        }
        <div className="main-content-info">
          <IntlMessages id="choiceAutomations.seniorityEntitlementShortInfo" />
          <Button type="primary" disabled={status === AddonsStatusEnum.CANCELED} onClick={createAuomation}>
            <IntlMessages id="automations.createAutomation" />
          </Button>
        </div>
        {isLoading ?
          <CircularProgress /> :
          <>
            {automations.length > 0 ?
              <Table
                dataSource={automations}
                columns={automationsColumns}
                className="clickable-table"
                onChange={(pagination, filters, sorter) => {
                  handleTableChange(pagination, filters, sorter as ITableSort)
                }}
                rowClassName={(record) => {
                  if(record.error) {
                    return 'clickable-table-error'
                  }
                  return ''
                }}
                onRow={(record) => {
                  if (record.error) {
                    return {onClick: () => { return }}
                  }
                  if (amIAdmin || authUserId === record.owner?.id) {
                    return {
                      onClick: () => history.push(`${FrontendUrls.addons}/${record.automationType.replace(/_/g, '-').toLowerCase()}/${record.id}/edit`),
                    }
                  } else {
                    return {onClick: () => {
                      message.error(formatMessage({ id: 'automations.cantOpenAutomations' }) as string)
                    }}
                  }
                }}
                rowKey={record => record.id}
                pagination={false}
              /> :
              <EmptyData
                title={<IntlMessages id="automations.emptyViewTitle" />}
                subTitle={<IntlMessages id="automations.emptyViewMessage" />}
              />
            }
          </>
        }
      </div>
    </div>
  )
}
export default SeniorityEntitlementView
